import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CheckCircle2, Circle, Sparkles, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";

export default function OnboardingWidget() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/onboarding/me");
      setData(data);
    } catch {
      setData({ tasks: [], completed_count: 0, total: 0 });
    }
  };

  useEffect(() => { load(); }, []);

  if (!data || data.total === 0) return null;
  const pct = Math.round((data.completed_count / data.total) * 100);
  // Once a user is 100%, hide the widget after a single load to avoid clutter.
  if (pct === 100) return null;

  const toggle = async (task) => {
    const done = !!task.completion;
    setData((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) => t.id === task.id ? { ...t, completion: done ? null : { completed_at: new Date().toISOString() } } : t),
      completed_count: prev.completed_count + (done ? -1 : 1),
    }));
    try {
      if (done) await api.delete(`/onboarding/me/${task.id}/complete`);
      else {
        await api.post(`/onboarding/me/${task.id}/complete`);
        toast.success(`✓ ${task.label}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5" data-testid="onboarding-widget">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center">
            <Sparkles className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">Your onboarding</h2>
            <div className="text-xs text-[#1E3A8A]">
              Welcome to HarborWell, {user?.name?.split(" ")[0] || "team"}! Knock these out at your own pace.
            </div>
          </div>
        </div>
        <button
          onClick={() => setExpanded((e) => !e)}
          data-testid="onboarding-toggle"
          className="text-xs text-[#1E3A8A] inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-[#F9F8F6]"
        >
          {expanded ? <>Hide <ChevronUp className="w-3 h-3" /></> : <>Show list <ChevronDown className="w-3 h-3" /></>}
        </button>
      </div>
      <div className="flex items-center gap-3 mb-1">
        <div className="flex-1 h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
          <div className="h-full bg-[#1E3A8A] transition-all" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs text-[#0F172A] font-semibold whitespace-nowrap">
          {data.completed_count} / {data.total} ({pct}%)
        </span>
      </div>
      {expanded && (
        <ul className="mt-3 space-y-1.5" data-testid="onboarding-list">
          {data.tasks.map((t) => {
            const done = !!t.completion;
            const isSignedOff = !!t.completion?.signed_off_by_name;
            return (
              <li
                key={t.id}
                data-testid={`onboarding-task-${t.id}`}
                className={`flex items-start gap-2 px-3 py-2 rounded-xl text-sm transition-colors ${
                  done ? "bg-[#F0FDF4]" : "bg-[#F9F8F6]"
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggle(t)}
                  data-testid={`onboarding-check-${t.id}`}
                  className="mt-0.5 flex-shrink-0"
                >
                  {done ? (
                    <CheckCircle2 className="w-4 h-4 text-[#166534]" strokeWidth={2} />
                  ) : (
                    <Circle className="w-4 h-4 text-[#1E3A8A]" strokeWidth={2} />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`${done ? "text-[#166534] line-through" : "text-[#0F172A]"} font-medium`}>
                      {t.label}
                    </span>
                    {t.required === false && (
                      <span className="text-[10px] text-[#7E937D] uppercase tracking-wider">Optional</span>
                    )}
                    {isSignedOff && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E40AF] inline-flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" strokeWidth={2} />
                        Signed off by {t.completion.signed_off_by_name}
                      </span>
                    )}
                  </div>
                  {t.description && (
                    <p className="text-xs text-[#334155] mt-0.5">{t.description}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
